// src/app/components/location-dropdown/location.dropdown.ts
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';

import {
  LOCATION_STORAGE_KEYS,
  LocationSelection,
  LocationSelectionService
} from '../../../pages/service/location-selection.service';
import { CityOption, CityService, Tier } from '../../../core/shared/city.service';
import { MeService } from '../../../core/shared/me.service';
import { T0BaselineService } from '../../../core/shared/t0-model/t0-baseline.service';

interface RouteLocationConfig {
  tier: Tier;
  feature: string;
  storageKeys: string[];
  /** Force this exact city and disable user change. */
  lockedCity?: { country: string; city: string };
  /** Exclude these cities from the list. */
  excludedCities?: { country: string; city: string }[];
  /** Ordered preference when auto-selecting. */
  preferredCities?: string[];
  /** T0: always select first available city on every entry. */
  alwaysFirst?: boolean;
  /** Whether the user is allowed to change the selection. */
  allowChange: boolean;
}

@Component({
  selector: 'app-location-dropdown',
  standalone: true,
  imports: [FormsModule, SelectModule, DialogModule],
  templateUrl: './location.dropdown.html'
})
export class LocationDropdownComponent implements OnInit, OnDestroy {
  private readonly baseline = inject(T0BaselineService);
  private readonly me = inject(MeService);
  private readonly router = inject(Router);

  private readonly destroy$ = new Subject<void>();

  private allItems: CityOption[] = [];
  private lastCommittedCity = '';
  private pendingSelection: CityOption | null = null;

  items: CityOption[] = [];
  selected: CityOption | null = null;
  confirmVisible = false;
  locationErrorMsg = '';
  allowChange = true;

  constructor(
    private readonly citySvc: CityService,
    public readonly selSvc: LocationSelectionService
  ) {}

  isCityLoading(): boolean {
    return this.selSvc.cityLoading();
  }

  async ngOnInit(): Promise<void> {
    this.selSvc.setCityLoading(true);
    await this.ensureMeReady();

    this.citySvc
      .getAll()
      .pipe(take(1))
      .subscribe({
        next: (items) => {
          this.allItems = items ?? [];
          this.applyRouteSelection();
          this.selSvc.setCityLoading(false);
        },
        error: () => {
          this.allItems = [];
          this.items = [];
          this.selected = null;
          this.locationErrorMsg = 'Unable to load city list.';
          this.selSvc.setCityLoading(false);
        }
      });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyRouteSelection());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChange(): void {
    if (!this.selected || !this.allowChange) {
      // Locked route: snap back to the locked selection.
      this.selected = this.items[0] ?? null;
      return;
    }

    const newCity = this.selected.city;
    const prevCity = this.lastCommittedCity;

    if (prevCity && newCity && !this.equals(prevCity, newCity)) {
      this.pendingSelection = this.selected;
      this.confirmVisible = true;
      this.selected = this.findByCityName(prevCity);
      return;
    }

    this.commitSelection(this.selected, this.currentConfig());
  }

  async confirmCityChange(): Promise<void> {
    this.confirmVisible = false;
    if (!this.pendingSelection) {
      return;
    }

    const next = this.pendingSelection;
    this.pendingSelection = null;
    const config = this.currentConfig();

    this.selSvc.setCityLoading(true);
    try {
      this.selected = next;
      this.commitSelection(next, config);

      if (config?.tier === 't0') {
        await this.baseline.resetAndReloadBaselineForCity(next.city);
      }
    } finally {
      this.selSvc.setCityLoading(false);
    }
  }

  cancelCityChange(): void {
    this.confirmVisible = false;
    this.pendingSelection = null;
  }

  // ---------------------------------------------------------------------------
  // Route resolution
  // ---------------------------------------------------------------------------

  private applyRouteSelection(): void {
    const config = this.currentConfig();

    if (!config || !this.allItems.length) {
      this.items = [];
      this.selected = null;
      this.allowChange = false;
      return;
    }

    this.allowChange = config.allowChange;
    this.confirmVisible = false;
    this.pendingSelection = null;
    this.locationErrorMsg = '';

    let filtered = this.getAccessibleItems(config.feature, config.tier);

    if (config.excludedCities?.length) {
      filtered = filtered.filter(
        (item) =>
          !config.excludedCities!.some(
            (ex) => this.equals(ex.city, item.city) && this.equals(ex.country, item.country)
          )
      );
    }

    if (config.lockedCity) {
      const locked =
        filtered.find(
          (item) =>
            this.equals(item.country, config.lockedCity!.country) &&
            this.equals(item.city, config.lockedCity!.city)
        ) ??
        filtered.find((item) => this.equals(item.city, config.lockedCity!.city)) ??
        null;

      this.items = locked ? [locked] : [];
      this.selected = locked;
      this.commitSelection(locked, config);
      return;
    }

    this.items = filtered;

    const chosen = this.pickSelection(filtered, config);
    this.selected = chosen;
    this.commitSelection(chosen, config);
  }

  private pickSelection(source: CityOption[], config: RouteLocationConfig): CityOption | null {
    if (config.preferredCities?.length) {
      const preferred = config.preferredCities
        .map((city) => source.find((item) => this.equals(item.city, city)) ?? null)
        .find((item): item is CityOption => !!item);
      if (preferred) {
        return preferred;
      }
    }

    // T0 requirement: always start from the first available city on entry.
    return source[0] ?? null;
  }

  private currentConfig(): RouteLocationConfig | null {
    const url = this.router.url || '';

    if (url.startsWith('/t0-dashboard')) {
      return {
        tier: 't0',
        feature: 'dashboard',
        storageKeys: [LOCATION_STORAGE_KEYS.t0, LOCATION_STORAGE_KEYS.legacy],
        excludedCities: [{ country: 'Brazil', city: 'Sao Paulo' }],
        alwaysFirst: true,
        allowChange: true
      };
    }

    if (url.startsWith('/enhanced-simulator')) {
      return {
        tier: 't3',
        feature: 'dashboard',
        storageKeys: [LOCATION_STORAGE_KEYS.t3],
        lockedCity: { country: 'Singapore', city: 'Singapore' },
        allowChange: false
      };
    }

    if (url.startsWith('/t6-dashboard')) {
      return {
        tier: 't6',
        feature: 'dashboard',
        storageKeys: [LOCATION_STORAGE_KEYS.t6],
        lockedCity: { country: 'Brazil', city: 'Sao Paulo' },
        allowChange: false
      };
    }

    if (url.startsWith('/economy-analysis')) {
      return {
        tier: 't6',
        feature: 'dashboard',
        storageKeys: [LOCATION_STORAGE_KEYS.economy],
        lockedCity: { country: 'Brazil', city: 'Sao Paulo' },
        allowChange: false
      };
    }

    if (url.startsWith('/file-upload')) {
      return {
        tier: 't3',
        feature: 'dashboard',
        storageKeys: [LOCATION_STORAGE_KEYS.fileUpload],
        excludedCities: [{ country: 'Singapore', city: 'Singapore' }],
        preferredCities: ['Lisbon', 'Brunei'],
        allowChange: true
      };
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Access + persistence
  // ---------------------------------------------------------------------------

  private commitSelection(sel: CityOption | null, config: RouteLocationConfig | null): void {
    if (!sel || !config) {
      this.lastCommittedCity = '';
      return;
    }

    const selection: LocationSelection = {
      country: sel.country,
      city: sel.city,
      cityId: sel.id,
      t0Available: sel.availability.t0,
      t3Available: sel.availability.t3,
      t6Available: sel.availability.t6,
      raw: sel.raw
    };

    this.selSvc.setSelected(selection, config.storageKeys);
    this.lastCommittedCity = sel.city;

    try {
      sessionStorage.setItem('cityNameFromMe', sel.city);
    } catch {}
  }

  private getAccessibleItems(feature: string, tier: Tier): CityOption[] {
    const allAccess = this.readAllAccessFromSession();
    const allowedNames = allAccess
      ? this.allItems.map((item) => item.city)
      : this.readMappedCitiesForFeatureTier(feature, tier);
    const allowedSet = new Set(allowedNames.map((n) => this.norm(n)));

    return this.allItems.filter((item) => {
      if (!item.availability[tier]) {
        return false;
      }
      return allAccess ? true : allowedSet.has(this.norm(item.city));
    });
  }

  private findByCityName(city: string): CityOption | null {
    return this.items.find((item) => this.equals(item.city, city)) ?? null;
  }

  private readMappedCitiesForFeatureTier(feature: string, tier: Tier): string[] {
    try {
      const raw = sessionStorage.getItem('userMappingFromMe');
      const parsed = raw ? JSON.parse(raw) : {};
      const values = parsed?.[feature]?.[tier.toUpperCase()] || [];
      return Array.isArray(values)
        ? Array.from(new Set(values.map((v: unknown) => String(v || '').trim()).filter(Boolean)))
        : [];
    } catch {
      return [];
    }
  }

  private readAllAccessFromSession(): boolean {
    return String(sessionStorage.getItem('allAccessFromMe') || '').trim().toLowerCase() === 'true';
  }

  private async ensureMeReady(): Promise<void> {
    const sessionId = this.ensureSessionId();
    const email = (sessionStorage.getItem('userEmail') || sessionStorage.getItem('userEmailFromMe') || '').trim();
    try {
      await this.me.checkAllowlist({ email, sessionId, forceRefresh: true });
    } catch (e) {
      console.error('[LocationDropdown] /me failed:', e);
    }
  }

  private ensureSessionId(): string {
    const key = 'session_id';
    let id = sessionStorage.getItem(key) || '';
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        sessionStorage.setItem(key, id);
      } catch {}
    }
    return id;
  }

  private norm(s: string): string {
    return String(s || '').trim().toLowerCase();
  }

  private equals(a: string, b: string): boolean {
    return this.norm(a) === this.norm(b);
  }
}